import { TrendingUp, TrendingDown, Send, Mail, MousePointer, Activity, BarChart2, Users, Plus, ArrowRight, RefreshCw } from "lucide-react";

/* ── Mock data ─────────────────────────────────────────────────── */
const VOLUME_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const VOLUME_DATA = [38400, 52100, 45600, 61200, 49800, 73400, 68900];

const STATS = [
  {
    label: "Emails Sent",
    value: "2,412,847",
    change: "+12.4%",
    up: true,
    icon: Send,
    iconBg: "var(--color-primary-tint)",
    iconColor: "var(--color-primary)",
  },
  {
    label: "Open Rate",
    value: "38.4%",
    change: "+3.1%",
    up: true,
    icon: Mail,
    iconBg: "var(--color-success-bg)",
    iconColor: "var(--color-success)",
  },
  {
    label: "Click Rate",
    value: "6.2%",
    change: "+0.8%",
    up: true,
    icon: MousePointer,
    iconBg: "var(--color-info-bg)",
    iconColor: "var(--color-info)",
  },
  {
    label: "Delivery Rate",
    value: "98.7%",
    change: "-0.1%",
    up: false,
    icon: Activity,
    iconBg: "var(--color-warning-bg)",
    iconColor: "var(--color-warning)",
  },
  {
    label: "Active Campaigns",
    value: "12",
    change: "+4",
    up: true,
    icon: BarChart2,
    iconBg: "var(--color-secondary-tint)",
    iconColor: "var(--color-secondary)",
  },
  {
    label: "Total Contacts",
    value: "34,218",
    change: "+8.2%",
    up: true,
    icon: Users,
    iconBg: "var(--color-info-bg)",
    iconColor: "var(--color-info)",
  },
];

const CAMPAIGNS = [
  { name: "Q2 Newsletter",              status: "sent",   sent: 45230, openRate: 42.1, clickRate: 7.8,  date: "Jun 25, 2026" },
  { name: "Product Launch: SwiftMail Pro", status: "sent", sent: 28100, openRate: 51.3, clickRate: 12.4, date: "Jun 23, 2026" },
  { name: "Weekly Digest #24",          status: "sent",   sent: 18750, openRate: 34.7, clickRate: 5.1,  date: "Jun 20, 2026" },
  { name: "Re-engagement Flow",         status: "active", sent: 12400, openRate: 28.9, clickRate: 4.3,  date: "Jun 18, 2026" },
  { name: "Welcome Series",             status: "active", sent: 3200,  openRate: 67.2, clickRate: 18.6, date: "Jun 15, 2026" },
  { name: "Flash Sale — Summer Prep",   status: "draft",  sent: 0,     openRate: 0,    clickRate: 0,    date: "Jun 14, 2026" },
];

const ACTIVITY = [
  { action: "Campaign sent",      detail: "Q2 Newsletter delivered to 45,230 contacts",        time: "2h ago",  type: "success" },
  { action: "New segment created", detail: "\"High-value customers\" — 3,812 contacts",          time: "5h ago",  type: "info" },
  { action: "A/B test completed", detail: "Subject line test — Variant B won (54.1% open rate)", time: "8h ago",  type: "success" },
  { action: "Campaign paused",    detail: "Weekly Digest #23 paused — review scheduled",         time: "1d ago",  type: "warning" },
  { action: "Import completed",   detail: "1,204 new contacts added from CSV upload",             time: "2d ago",  type: "info" },
];

/* ── Activity type config ──────────────────────────────────────── */
const ACTIVITY_COLOR = {
  success: { dot: "var(--color-success)", bg: "var(--color-success-bg)" },
  info:    { dot: "var(--color-info)",    bg: "var(--color-info-bg)" },
  warning: { dot: "var(--color-warning)", bg: "var(--color-warning-bg)" },
  danger:  { dot: "var(--color-danger)",  bg: "var(--color-danger-bg)" },
};

/* ── Status badge config ───────────────────────────────────────── */
const STATUS_MAP = {
  sent:   { label: "Sent",   bg: "var(--color-success-bg)",  border: "var(--color-success-border)",  color: "var(--color-success)" },
  active: { label: "Active", bg: "var(--color-info-bg)",     border: "var(--color-info-border)",     color: "var(--color-info)" },
  draft:  { label: "Draft",  bg: "var(--color-surface-alt)", border: "var(--color-border)",          color: "var(--color-text-muted)" },
  paused: { label: "Paused", bg: "var(--color-warning-bg)",  border: "var(--color-warning-border)",  color: "var(--color-warning)" },
};

/* ── Sub-components ────────────────────────────────────────────── */
function AreaChart({ data }) {
  const W = 340, H = 90;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => [
    (i / (data.length - 1)) * W,
    H - ((v - min) / range) * (H - 20) - 10,
  ]);
  const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${line} L${W},${H} L0,${H} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 90 }} aria-hidden="true">
      <defs>
        <linearGradient id="dashAreaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.22" />
          <stop offset="100%" stopColor="var(--color-primary)" stopOpacity="0.01" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#dashAreaGrad)" />
      <path d={line} fill="none" stroke="var(--color-primary)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="3.5" fill="var(--color-primary)" stroke="var(--color-surface)" strokeWidth="1.5" />
      ))}
    </svg>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] ?? STATUS_MAP.draft;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border"
      style={{ background: s.bg, borderColor: s.border, color: s.color }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
      {s.label}
    </span>
  );
}

function StatCard({ label, value, change, up, icon: Icon, iconBg, iconColor }) {
  return (
    <div
      className="bg-surface border border-border rounded-xl p-5 flex flex-col gap-3"
      style={{ boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}
    >
      <div className="flex items-start justify-between">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: iconBg }}
        >
          <Icon className="w-4 h-4" style={{ color: iconColor }} />
        </div>
        <span
          className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
          style={{
            background: up ? "var(--color-success-bg)" : "var(--color-danger-bg)",
            color: up ? "var(--color-success)" : "var(--color-danger)",
          }}
        >
          {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {change}
        </span>
      </div>
      <div>
        <div className="text-2xl font-bold text-text_primary tracking-tight leading-none">{value}</div>
        <div className="text-xs text-text_muted mt-1">{label}</div>
      </div>
    </div>
  );
}

/* ── Dashboard page ────────────────────────────────────────────── */
export default function Dashboard() {
  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  const topCampaigns = [...CAMPAIGNS]
    .filter((c) => c.sent > 0)
    .sort((a, b) => b.openRate - a.openRate);

  return (
    <div className="space-y-6">

      {/* ── Page header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-text_primary">Good morning, Admin</h1>
          <p className="text-sm text-text_muted mt-0.5">{today} · Here's your marketing overview.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-2 text-sm font-medium px-3.5 py-2 rounded-xl border border-border bg-surface text-text_secondary hover:bg-surface_alt transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-xl text-white"
            style={{
              background: "linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)",
              boxShadow: "0 4px 16px rgba(53,53,93,0.22)",
            }}
          >
            <Plus className="w-4 h-4" />
            New Campaign
          </button>
        </div>
      </div>

      {/* ── KPI stat grid ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
        {STATS.map((s) => (
          <StatCard key={s.label} {...s} />
        ))}
      </div>

      {/* ── Chart row ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

        {/* Email volume chart */}
        <div
          className="lg:col-span-3 bg-surface border border-border rounded-xl p-5"
          style={{ boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}
        >
          <div className="flex items-start justify-between mb-1">
            <div>
              <h3 className="text-sm font-semibold text-text_primary">Email Volume</h3>
              <p className="text-xs text-text_muted mt-0.5">Last 7 days</p>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-text_primary">
                {VOLUME_DATA.reduce((a, b) => a + b, 0).toLocaleString()}
              </div>
              <div className="text-xs text-text_muted">total emails</div>
            </div>
          </div>
          <div className="mt-3">
            <AreaChart data={VOLUME_DATA} />
          </div>
          <div className="flex justify-between mt-1 px-0.5">
            {VOLUME_DAYS.map((d) => (
              <span key={d} className="text-xs text-text_muted">{d}</span>
            ))}
          </div>
        </div>

        {/* Top campaigns by open rate */}
        <div
          className="lg:col-span-2 bg-surface border border-border rounded-xl p-5"
          style={{ boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}
        >
          <h3 className="text-sm font-semibold text-text_primary mb-4">Top by Open Rate</h3>
          <div className="space-y-4">
            {topCampaigns.map((c, i) => (
              <div key={c.name}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="text-xs font-bold w-4 flex-shrink-0"
                      style={{ color: i === 0 ? "var(--color-primary)" : "var(--color-text-muted)" }}
                    >
                      {i + 1}
                    </span>
                    <span className="text-xs text-text_secondary truncate" title={c.name}>
                      {c.name}
                    </span>
                  </div>
                  <span className="text-xs font-bold text-text_primary ml-3 flex-shrink-0">{c.openRate}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-surface_alt overflow-hidden ml-6">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${(c.openRate / 70) * 100}%`,
                      background: i === 0
                        ? "linear-gradient(90deg, var(--color-primary) 0%, var(--color-secondary) 100%)"
                        : "var(--color-border-strong)",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Recent campaigns + Activity feed ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">

        {/* Recent campaigns table */}
        <div
          className="xl:col-span-2 bg-surface border border-border rounded-xl overflow-hidden"
          style={{ boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <h3 className="text-sm font-semibold text-text_primary">Recent Campaigns</h3>
            <button
              type="button"
              className="text-xs font-semibold inline-flex items-center gap-1 transition-colors hover:opacity-80"
              style={{ color: "var(--color-primary)" }}
            >
              View all <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-surface_alt">
                  <th className="text-left text-xs font-semibold text-text_muted uppercase tracking-wider px-5 py-3">Campaign</th>
                  <th className="text-left text-xs font-semibold text-text_muted uppercase tracking-wider px-4 py-3">Status</th>
                  <th className="text-right text-xs font-semibold text-text_muted uppercase tracking-wider px-4 py-3">Sent</th>
                  <th className="text-right text-xs font-semibold text-text_muted uppercase tracking-wider px-4 py-3">Open %</th>
                  <th className="text-right text-xs font-semibold text-text_muted uppercase tracking-wider px-5 py-3">Click %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {CAMPAIGNS.map((c) => (
                  <tr key={c.name} className="hover:bg-surface_alt transition-colors cursor-pointer">
                    <td className="px-5 py-3.5">
                      <div className="text-sm font-medium text-text_primary leading-tight">{c.name}</div>
                      <div className="text-xs text-text_muted mt-0.5">{c.date}</div>
                    </td>
                    <td className="px-4 py-3.5">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <span className="text-sm text-text_secondary">
                        {c.sent > 0 ? c.sent.toLocaleString() : "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <span
                        className="text-sm font-semibold"
                        style={{ color: c.openRate > 0 ? "var(--color-success)" : "var(--color-text-muted)" }}
                      >
                        {c.openRate > 0 ? `${c.openRate}%` : "—"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <span
                        className="text-sm font-semibold"
                        style={{ color: c.clickRate > 0 ? "var(--color-info)" : "var(--color-text-muted)" }}
                      >
                        {c.clickRate > 0 ? `${c.clickRate}%` : "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Activity feed */}
        <div
          className="bg-surface border border-border rounded-xl"
          style={{ boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <h3 className="text-sm font-semibold text-text_primary">Recent Activity</h3>
          </div>
          <div className="p-5 space-y-4">
            {ACTIVITY.map((a, i) => {
              const c = ACTIVITY_COLOR[a.type] ?? ACTIVITY_COLOR.info;
              return (
                <div key={i} className="flex gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center"
                      style={{ background: c.bg }}
                    >
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ background: c.dot }}
                      />
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-text_primary leading-tight">{a.action}</div>
                    <div className="text-xs text-text_muted mt-0.5 leading-snug">{a.detail}</div>
                    <div className="text-xs text-text_muted mt-1 font-medium">{a.time}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quick actions */}
          <div className="px-5 pb-5 pt-1 border-t border-border mt-1">
            <p className="text-xs font-semibold text-text_muted uppercase tracking-wider mb-3">Quick Actions</p>
            <div className="space-y-2">
              {[
                { label: "Create Campaign", color: "var(--color-primary)" },
                { label: "Import Contacts", color: "var(--color-success)" },
                { label: "View Analytics",  color: "var(--color-info)" },
              ].map((qa) => (
                <button
                  key={qa.label}
                  type="button"
                  className="w-full text-left text-xs font-semibold px-3 py-2.5 rounded-lg border border-border hover:bg-surface_alt transition-colors flex items-center justify-between"
                >
                  <span className="text-text_secondary">{qa.label}</span>
                  <ArrowRight className="w-3.5 h-3.5" style={{ color: qa.color }} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
